// SiteUP CRM (2026-09-22): leitura, no front, de qual regra de IA do Kanban
// vale para um funil. Regra decidida (backend na fatia 4a): vale a regra do
// funil quando `settings.ai_auto_create.enabled` está ligado nele; senão
// vale o padrão da conta (`account.settings.kanban_ai_auto_create`), se ele
// estiver ligado e cobrir o funil (`funnel_ids` vazio = todos os funis).
// Lógica pura: nada de Vue, nada de API.

// Disparado pelo wizard do padrão da conta depois de salvar, para quem mostra
// o que o funil herda atualizar sem recarregar a página.
export const KANBAN_AI_ACCOUNT_RULE_SAVED = 'kanbanAiAccountRuleSaved';

export const ACCOUNT_RULE_STATUS = Object.freeze({
  OFF: 'off',
  NOT_COVERED: 'not_covered',
  COVERS: 'covers',
});

export const EXTRACT_FIELD_KEYS = Object.freeze([
  'title',
  'description',
  'priority',
  'value',
]);

const MIN_HOURS = 1;
const MAX_HOURS = 168;
const DEFAULT_HOURS = 6;

const normalizeHours = raw => {
  const hours = Math.round(Number(raw));
  if (!Number.isFinite(hours) || hours <= 0) return DEFAULT_HOURS;
  return Math.min(Math.max(hours, MIN_HOURS), MAX_HOURS);
};

// Mesmos padrões do wizard (FunnelsGlobalAiSettings): conta nova vem sem
// extract_fields e, nesse caso, todos os campos contam como marcados.
const normalizeExtractFields = fields => {
  const source = fields && typeof fields === 'object' ? fields : {};
  const hasAny = Object.keys(source).length > 0;
  return EXTRACT_FIELD_KEYS.reduce((acc, key) => {
    acc[key] = hasAny ? Boolean(source[key]) : true;
    return acc;
  }, {});
};

export const normalizeAccountAiRule = stored => {
  const source = stored && typeof stored === 'object' ? stored : {};
  return {
    enabled: Boolean(source.enabled),
    funnel_ids: Array.isArray(source.funnel_ids) ? [...source.funnel_ids] : [],
    routing_prompt: source.routing_prompt || '',
    custom_prompt: source.custom_prompt || '',
    extract_fields: normalizeExtractFields(source.extract_fields),
    attach_offers: Boolean(source.attach_offers),
    auto_update_enabled: Boolean(source.auto_update_enabled),
    auto_update_after_hours: normalizeHours(source.auto_update_after_hours),
  };
};

const hasFunnelId = funnelId =>
  funnelId !== null && funnelId !== undefined && funnelId !== '';

// Lista vazia cobre todos os funis, inclusive um funil ainda não salvo.
export const accountRuleCoversFunnel = (rule, funnelId) => {
  const ids = Array.isArray(rule?.funnel_ids) ? rule.funnel_ids : [];
  if (!ids.length) return true;
  if (!hasFunnelId(funnelId)) return false;
  return ids.some(id => String(id) === String(funnelId));
};

// O que o funil recebe quando NÃO tem regra própria.
export const accountRuleStatus = (rule, funnelId) => {
  if (!rule?.enabled) return ACCOUNT_RULE_STATUS.OFF;
  return accountRuleCoversFunnel(rule, funnelId)
    ? ACCOUNT_RULE_STATUS.COVERS
    : ACCOUNT_RULE_STATUS.NOT_COVERED;
};

// SiteUP CRM (2026-09-23): selo de cada funil na tela do padrão da conta.
// Mesma regra do resolver: regra própria ligada no funil vence; senão o funil
// usa o padrão quando está coberto por ele (lista vazia = todos).
export const FUNNEL_AI_BADGE = Object.freeze({
  OWN: 'own',
  ACCOUNT: 'account',
  OUTSIDE: 'outside',
});

export const funnelHasOwnAiRule = funnel =>
  Boolean(funnel?.settings?.ai_auto_create?.enabled);

export const funnelAiBadge = (funnel, rule) => {
  if (funnelHasOwnAiRule(funnel)) return FUNNEL_AI_BADGE.OWN;
  return accountRuleCoversFunnel(rule, funnel?.id)
    ? FUNNEL_AI_BADGE.ACCOUNT
    : FUNNEL_AI_BADGE.OUTSIDE;
};

// Espelha Kanban::AiConfigResolver: qual regra vale para o card deste funil
// ('funnel', 'account' ou null). `funnel` pode faltar na store (funil
// inativo): aí só o padrão da conta decide, pelo `funnelId` do card.
export const effectiveAiRuleSource = ({ funnel, funnelId, accountRule }) => {
  if (funnelHasOwnAiRule(funnel)) return 'funnel';
  const id = hasFunnelId(funnelId) ? funnelId : funnel?.id;
  if (!hasFunnelId(id)) return null;
  return accountRuleStatus(accountRule, id) === ACCOUNT_RULE_STATUS.COVERS
    ? 'account'
    : null;
};

// Criar card com IA segue o controller (create_ai_card): com o padrão da
// conta ligado, o backend escolhe o funil e ignora o que o cliente mandar;
// senão precisa de um funil com regra própria.
export const canCreateCardWithAi = ({ funnels, accountRule }) =>
  Boolean(accountRule?.enabled) ||
  (Array.isArray(funnels) && funnels.some(funnelHasOwnAiRule));
